import { useMemo } from "react";

/**
 * Field-advancement for the score sheet, driven by an EXPLICIT ordered field
 * list derived from formData — not by scraping the DOM for order.
 *
 * The list is built in the exact same order the form renders cells:
 *   group scores by match type (in first-seen order, same as the render's
 *   `scoresByType`), then for each score in a group, for each stage, emit
 *   [Score] then [X].  Because that mirrors document order, advancement stays
 *   identical to the previous querySelectorAll-based implementation, but a
 *   markup reshuffle can no longer silently reorder the keyboard/keypad path.
 *
 * Focus itself is still applied via a *targeted* DOM lookup (by the cell's
 * data-* identity, not by position) so we keep the select-all-on-focus sheet
 * workflow and let the input's own onFocus update `activeField`.
 */
function buildFieldOrder(scores, matchConfig) {
  if (!scores || !matchConfig) return [];
  // Group score-card indices by match type, preserving first-seen order.
  const groups = new Map();
  scores.forEach((score, scoreIndex) => {
    const matchTypeObj = matchConfig.match_types.find(
      (mt) => mt.instance_name === score.match_type_instance
    );
    if (!matchTypeObj) return; // cards without a config match render no inputs
    const key = matchTypeObj.type;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(scoreIndex);
  });

  const order = [];
  groups.forEach((indices) => {
    indices.forEach((scoreIndex) => {
      scores[scoreIndex].stages.forEach((stage, stageIndex) => {
        order.push({ scoreIndex, stageIndex, field: "score" });
        order.push({ scoreIndex, stageIndex, field: "x_count" });
      });
    });
  });
  return order;
}

const useScoreEntryNavigation = ({
  scores,
  matchConfig,
  formRootRef,
  activeField,
  setActiveField,
}) => {
  const fieldOrder = useMemo(
    () => buildFieldOrder(scores, matchConfig),
    [scores, matchConfig]
  );

  const indexOfField = (f) =>
    f
      ? fieldOrder.findIndex(
          (o) =>
            o.scoreIndex === f.scoreIndex &&
            o.stageIndex === f.stageIndex &&
            o.field === f.field
        )
      : -1;

  const findInput = (f) => {
    if (!f || !formRootRef.current) return null;
    return formRootRef.current.querySelector(
      `input[data-score-nav="1"][data-score-index="${f.scoreIndex}"]` +
        `[data-stage-index="${f.stageIndex}"][data-field="${f.field}"]`
    );
  };

  const focusField = (f) => {
    const el = findInput(f);
    if (!el) return;
    el.focus();
    // Select contents for quick overwrite (score sheet workflow)
    if (typeof el.select === "function") el.select();
  };

  // Enter/Tab-forward: focus the next cell after the given DOM input.
  const focusNextFromElement = (el) => {
    if (!el) return;
    const scoreIndex = parseInt(el.dataset.scoreIndex, 10);
    const stageIndex = parseInt(el.dataset.stageIndex, 10);
    const field = el.dataset.field;
    const idx = indexOfField({ scoreIndex, stageIndex, field });
    if (idx >= 0 && idx < fieldOrder.length - 1) {
      focusField(fieldOrder[idx + 1]);
    }
  };

  // Keypad flow: move to the next cell; close the pad past the last cell.
  const advanceFromActive = () => {
    if (!activeField) return;
    const idx = indexOfField(activeField);
    if (idx >= 0 && idx < fieldOrder.length - 1) {
      focusField(fieldOrder[idx + 1]); // onFocus updates activeField
    } else {
      if (idx >= 0) {
        const el = findInput(activeField);
        if (el) el.blur();
      }
      setActiveField(null);
    }
  };

  // Tap flow: jump to the next stage's Score cell (skip the current X, which
  // tap mode derives). Falls back to closing the pad past the last stage.
  const advanceToNextStage = () => {
    if (!activeField) return;
    const idx = indexOfField(activeField);
    const nextScoreIdx = fieldOrder.findIndex(
      (o, i) => i > idx && o.field === "score"
    );
    if (nextScoreIdx >= 0) {
      focusField(fieldOrder[nextScoreIdx]);
    } else {
      if (idx >= 0) {
        const el = findInput(activeField);
        if (el) el.blur();
      }
      setActiveField(null);
    }
  };

  return {
    fieldOrder,
    focusField,
    focusNextFromElement,
    advanceFromActive,
    advanceToNextStage,
  };
};

export default useScoreEntryNavigation;
